import * as React from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { cn } from "../lib/utils";

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  icon?: React.ReactNode; // Optional icon above the title
  iconColor?:
    | "red"
    | "green"
    | "blue"
    | "yellow"
    | "indigo"
    | "purple"
    | "pink"
    | "gray"; // Add more colors as needed
  children: React.ReactNode;
  actions?: React.ReactNode; // Optional for custom actions
}

const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  description,
  icon,
  iconColor = "gray",
  children,
  actions,
}) => {
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-white text-center rounded-lg p-6 shadow-lg max-w-md mx-auto">
        {icon && (
          <div className="mb-4 flex justify-center">
            <div
              className={cn("p-4 rounded-full", {
                "bg-red-100": iconColor === "red",
                "bg-green-100": iconColor === "green",
                "bg-blue-100": iconColor === "blue",
                "bg-yellow-100": iconColor === "yellow",
                "bg-indigo-100": iconColor === "indigo",
                "bg-purple-100": iconColor === "purple",
                "bg-pink-100": iconColor === "pink",
                "bg-gray-100": iconColor === "gray",
              })}
            >
              {React.cloneElement(icon as React.ReactElement, {
                className: cn(
                  "h-8 w-8",
                  {
                    "text-red-500": iconColor === "red",
                    "text-green-500": iconColor === "green",
                    "text-blue-500": iconColor === "blue",
                    "text-yellow-500": iconColor === "yellow",
                    "text-indigo-500": iconColor === "indigo",
                    "text-purple-500": iconColor === "purple",
                    "text-pink-500": iconColor === "pink",
                    "text-gray-500": iconColor === "gray",
                  },
                  (icon as React.ReactElement)?.props?.className
                ),
              })}
            </div>
          </div>
        )}
        <DialogHeader>
          <DialogTitle className="text-center text-xl font-semibold">
            {title}
          </DialogTitle>
          {description && (
            <DialogDescription className="text-center text-gray-500 mt-1">
              {description}
            </DialogDescription>
          )}
        </DialogHeader>
        <div className="mt-0">{children}</div>
        {actions && (
          <DialogFooter className="mt-2 flex justify-between gap-0">
            {actions}
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default Modal;
